
import { useState, useEffect } from "react";
import { recipeService } from "@/services/recipe";
import { Recipe } from "@/types/recipe";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface RecipeSuccessData {
  id: string;
  title: string;
}

interface UseRecipeUrlFormProps {
  onRecipeAdded: () => void;
  initialUrl?: string;
  suggestionId?: string;
}

export const useRecipeUrlForm = ({ onRecipeAdded, initialUrl = "", suggestionId }: UseRecipeUrlFormProps) => {
  const [url, setUrl] = useState(initialUrl);
  const [isLoading, setIsLoading] = useState(false);
  const [processingStage, setProcessingStage] = useState<string>("");
  const [progress, setProgress] = useState(0);
  const [successData, setSuccessData] = useState<RecipeSuccessData | null>(null);

  useEffect(() => {
    // Update URL if initialUrl changes
    setUrl(initialUrl);
  }, [initialUrl]);

  // Simulate progress bar advancement during loading
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (isLoading) {
      // Start at current progress or 10%
      setProgress(prev => prev || 10);
      
      interval = setInterval(() => {
        setProgress(prev => {
          // Advance slower as we get closer to 90%
          const increment = prev < 30 ? 5 : prev < 60 ? 3 : 1;
          // Cap at 90% until complete
          return Math.min(90, prev + increment);
        });
      }, 800);
    } else {
      // When loading completes, either go to 100% or back to 0
      setProgress(isLoading === false ? 0 : 100);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isLoading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!url.trim()) {
      toast.error("Please enter a URL");
      return;
    }
    
    try {
      setIsLoading(true);
      setProcessingStage("Fetching webpage content...");
      
      // Use the scrape-recipe edge function
      const { data, error } = await supabase.functions.invoke('scrape-recipe', {
        body: { url }
      });
      
      if (error) {
        console.error("Error from scrape-recipe function:", error);
        toast.error("Error scraping recipe: " + error.message);
        setIsLoading(false);
        return;
      }
      
      if (!data || !data.recipe) {
        toast.error("Could not extract recipe from the provided URL");
        setIsLoading(false);
        return;
      }
      
      const recipe: Recipe = data.recipe;
      
      setProcessingStage("Saving recipe to database...");
      // Save recipe to database
      try {
        const savedRecipe = await recipeService.saveRecipe(recipe, suggestionId);
        
        if (savedRecipe) {
          // Set success data for dialog
          setSuccessData({
            id: savedRecipe.id,
            title: recipe.title
          });
          
          // Refresh recipes list using the provided callback
          onRecipeAdded();
          
          // Clear form
          setUrl("");
        }
      } catch (error: any) {
        console.error("Error saving recipe:", error);
        toast.error("Error saving recipe. Please try again.");
      }
    } catch (error) {
      console.error("Error scraping recipe:", error);
      toast.error("Error extracting recipe. Please try again.");
    } finally {
      setIsLoading(false);
      setProcessingStage("");
    }
  };

  const handleDialogClose = () => {
    setSuccessData(null);
  };

  // Auto-submit the form when initialUrl is provided and changed
  useEffect(() => {
    if (initialUrl && initialUrl.trim() !== "" && !isLoading) {
      // Use a slight delay to allow the component to fully render
      const timer = setTimeout(() => {
        handleSubmit(new Event('submit') as unknown as React.FormEvent);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [initialUrl]); // Only depend on initialUrl

  return {
    url,
    setUrl,
    isLoading,
    processingStage,
    progress,
    successData,
    handleSubmit,
    handleDialogClose
  };
};
